/**
 * useTier Hook
 *
 * Provides access to the current user's tier information.
 * Uses the user profile data to return tier-related information.
 *
 * This hook leverages TanStack Query to fetch and cache tier data.
 */

import { useQuery } from "@tanstack/react-query";
import { userService, UserProfileResponse } from "../api/services/user.service";

export interface UseTierReturn {
  /**
   * User's current tier (1-4)
   */
  tier: number;
  /**
   * Human-readable tier name
   */
  tierName: string;
  /**
   * True if tier data is still loading
   */
  isLoading: boolean;
  /**
   * Error if tier fetch failed
   */
  error: Error | null;
  /**
   * True if user can upgrade (tier < 4)
   */
  canUpgrade: boolean;
  /**
   * Next tier level (null if tier = 4)
   */
  nextTier: number | null;
  /**
   * Next tier name (null if tier = 4)
   */
  nextTierName: string | null;
  /**
   * Full user profile data
   */
  profile: UserProfileResponse | undefined;
}

/**
 * Helper function to get tier name from tier number
 */
export function getTierName(tier: number): string {
  const tierNames: Record<number, string> = {
    1: "Unverified",
    2: "Verified",
    3: "Verified",
    4: "Verified",
  };
  return tierNames[tier] || "Unverified";
}

/**
 * Helper function to get tier color for styling
 */
export function getTierColor(tier: number): string {
  const tierColors: Record<number, string> = {
    1: "gray",
    2: "orange",
    3: "amber",
    4: "orange",
  };
  return tierColors[tier] || "gray";
}

/**
 * Helper function to check if user can upgrade
 */
export function canUpgrade(tier: number): boolean {
  // Master §6: getting verified is the only self-serve step
  return tier < 2;
}

/**
 * Hook to access current user's tier information
 *
 * @returns Tier information and status
 *
 * @example
 * ```tsx
 * function ProfilePage() {
 *   const { tier, tierName, canUpgrade, isLoading } = useTier();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>Your Tier: {tierName}</h1>
 *       {canUpgrade && <UpgradeButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTier(): UseTierReturn {
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: userService.getProfile,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - matches useRequireProfile
  });

  const tier = profile?.tier || 1;
  const tierName = profile?.tierName || getTierName(tier);
  const userCanUpgrade = canUpgrade(tier);
  const nextTier = userCanUpgrade ? tier + 1 : null;
  const nextTierName = nextTier ? getTierName(nextTier) : null;

  return {
    tier,
    tierName,
    isLoading,
    error: isError ? (error as Error) : null,
    canUpgrade: userCanUpgrade,
    nextTier,
    nextTierName,
    profile,
  };
}